import { assign } from "xstate";
import {
  AR_EVENTS,
  AR_POSITION_SEEK,
  ASSET_EVENTS,
  PAGES,
  ASSET_TYPES,
  XR_MODES,
  AR_PLATFORM,
} from "../constants";
import { sendAutoEvent } from "../utils/AnalyticsUtil";
import { generateUIConfig } from "../modules/MachineHelper";
import {
  filterAssetObjects,
  setUpScene,
  getCorrelationData,
  getXrModefromURL,
} from "../modules/MachineUtils";
import WaitSM from "./WaitSM";
import { recenterScene } from "../modules/ThreejsUtility";

export const AR_SM_CONST = {
  // States
  LOADING_HDRI: "LOADING_HDRI",
  INITIALIZING: "INITIALIZING",
  WEBXR_ACTIVATION: "WEBXR_ACTIVATION",
  POSITION_SEEK_START: "POSITION_SEEK_START",
  WAIT_FOR_MODEL_LOAD: "WAIT_FOR_MODEL_LOAD",
  ON_LOAD_AR: "ON_LOAD_AR",
  POSITION_SEEKING: "POSITION_SEEKING",
  POSITION_SET_DONE: "POSITION_SET_DONE",
  RECENTER_READY: "RECENTER_READY",
  // Misc
  ID: "AR_SM",
};

const getPositionTextureId = (actions, skuId) =>
  actions.ConfigLoader.getSKUConfig(skuId).positionTexture;

const getPositionTexture = (actions, skuId) => {
  return actions.AssetLoader.getAsset(getPositionTextureId(actions, skuId));
};

const findHdriConfig = (actions, skuId) => {
  return filterAssetObjects(actions.ConfigLoader.getSKUConfig(skuId).assets, [
    ASSET_TYPES.HDRI,
    ASSET_TYPES.CUBE_MAP,
  ]);
};

const checkHDRIAndPositionTextureLoaded = (actions, skuId) => {
  return {
    conds: [
      () => actions.ConfigLoader.getSKUConfig(skuId) != null,
      () => {
        if (!actions.ConfigLoader.getSKUConfig(skuId)) return false;
        const positionTextureId = getPositionTextureId(actions, skuId);
        return (
          actions.AssetLoader.getAsset(positionTextureId) != null &&
          actions.AssetLoader.isAssetObjectsLoaded(
            findHdriConfig(actions, skuId)
          )
        );
      },
    ],
    events: [ASSET_EVENTS.ASSET_LOADED, ASSET_EVENTS.CONFIG_LOADED],
  };
};

const getSkuAsset = (actions, skuId, modelId = "abcde") => {
  const skuObjectId = actions.ConfigLoader.getSKUConfig(skuId).models.path;
  const getModelFn = actions.ARModels.getModel;
  const model = getModelFn(modelId);
  let modelMain;
  if (model != null && model) {
    modelMain = model;
  } else {
    const assetObject = actions.AssetLoader.getAsset(skuObjectId);

    if (assetObject) {
      const clonedObject = actions.ARModels.cloneandAddModel(
        assetObject,
        modelId
      );
      modelMain = clonedObject;
    }
  }
  return modelMain;
};

// const getAssetHDRI = (actions, skuId) => {
//   return actions.AssetLoader.getAsset(
//     Object.keys(findHdriConfig(actions, skuId))[0]
//   );
// };

const hitTestCallback =
  (actions) =>
  ({ position, rotation }) => {
    const model = actions.PositionReticle.get();
    model.scale.set(1, 1, 1);
    // model.material.transparent = false;
    // Increasing the y coordinate slightly so that the reticle is slightly above floor.
    // Otherwise it starts merging with it.
    model.position.set(position.x, position.y + 0.001, position.z);
    model.rotation.set(rotation.x, rotation.y, rotation.z);
  };

const dragFunctionality = (modelId, actions, sceneDetails) => {
  return (event) => {
    console.log(
      event,
      modelId,
      "dragFunctionality",
      actions.ARModels.getModel(modelId)
    );
    actions.touchEvents.touchHandler(
      () => actions.ARModels.getPositonOfModel(modelId),
      (positionToSet) =>
        actions.ARModels.setPositonOfModel(modelId, positionToSet),
      (positionToSet) =>
        actions.ThreejsScene.setLightCameraPosition(
          positionToSet,
          actions.ARModels.getModel(modelId)
        ),
      () => actions.ARModels.getRotationOfModel(modelId),
      (rotationToSet) =>
        actions.ARModels.setRotationOfModel(modelId, rotationToSet),
      event,
      sceneDetails,
      (positionY) => actions.EighthWall.setOpacity(positionY)
    );
  };
};

const addPositionReticleToScene = (actions) => {
  const positionReticle = actions.PositionReticle.get();
  positionReticle.scale.set(0, 0, 0); // Make it invisible
  actions.ARPlatform.add(positionReticle);
};

const createPositionReticle = (actions, skuId) => {
  actions.PositionReticle.create(
    getPositionTexture(actions, skuId),
    actions.ARPlatform.getScale()
  );
};

const setPositionOnSku = (actions, skuId, modelId) => {
  console.log("Set position on SKU");
  let scale = actions.ARPlatform.getScale();
  const arScale = actions.ConfigLoader.getSKUConfig(skuId).models.config?.scale;
  if (arScale) {
    scale = Number(scale);
    scale *= arScale;
  }
  const reticle = actions.PositionReticle.get();
  const skuObject = getSkuAsset(actions, skuId, modelId).scene;
  const pos = reticle.position;
  const rot = reticle.rotation;

  skuObject.position.set(pos.x, pos.y, pos.z);
  skuObject.rotation.set(rot.x, rot.y, rot.z, rot.w);
  skuObject.scale.set(scale, scale, scale);
  actions.ARPlatform.add(skuObject);
  actions.ARPlatform.remove(reticle);
  actions.ThreejsScene.setLightCameraPosition(
    pos,
    actions.ARModels.getModel(modelId)
  );
};

const removeSku = (actions, skuId, modelId) => {
  // const skuObject = actions.AssetLoader.getAsset(skuId);
  const skuObject = getSkuAsset(actions, skuId, modelId).scene;
  actions.ARPlatform.remove(skuObject);
};

export const AR_SM = (actions, skuId) => ({
  id: AR_SM_CONST.ID,
  initial: AR_SM_CONST.LOADING_HDRI,
  context: {
    modelId: null,
  },
  states: {
    [AR_SM_CONST.LOADING_HDRI]: {
      entry: [
        () => actions.RendererState().setPage(PAGES.AR),
        () => actions.ARPageState().setShowLoading(true),
        assign({ modelId: "abcde" }),
      ],
      ...WaitSM(checkHDRIAndPositionTextureLoaded(actions, skuId)),
      onDone: AR_SM_CONST.INITIALIZING,
    },
    [AR_SM_CONST.INITIALIZING]: {
      entry: [
        () => actions.RendererState().setPage(PAGES.AR),
        () => createPositionReticle(actions, skuId),
        // () => actions.ARPlatform.startAR(getAssetHDRI(actions, skuId)),
        () =>
          actions.ARPlatform.startAR(
            setUpScene(
              actions,
              skuId,
              actions.ConfigLoader,
              XR_MODES.AR,
              actions.ARPlatform.getPlatform()
            )
          ),
        () => actions.ARPageState().setShowLoading(false),
        () => actions.ARPageState().setShowCalibration(true),
      ],
      on: {
        [AR_EVENTS.INITIALIZED]: AR_SM_CONST.POSITION_SEEK_START,
        [AR_EVENTS.WEBXR_EXCEPTION]: AR_SM_CONST.WEBXR_ACTIVATION,
      },
    },
    [AR_SM_CONST.WEBXR_ACTIVATION]: {
      entry: [() => actions.RendererState().setPage(PAGES.WEBXR_ACTIVATION)],
      on: {
        [XR_MODES.AR]: AR_SM_CONST.INITIALIZING,
      },
    },
    [AR_SM_CONST.POSITION_SEEK_START]: {
      entry: [
        () => actions.ARControllerUIState().setEnableARSwitch(true),
        () => actions.ARPlatform.startHitTest(hitTestCallback(actions)),
        // () => addPositionReticleToScene(actions),
      ],
      on: {
        [AR_POSITION_SEEK.SEEKING]: AR_SM_CONST.WAIT_FOR_MODEL_LOAD,
      },
    },
    [AR_SM_CONST.WAIT_FOR_MODEL_LOAD]: {
      ...WaitSM({
        conds: [() => getSkuAsset(actions, skuId) != null],
        events: [ASSET_EVENTS.ASSET_LOADED],
      }),
      onDone: [
        {
          target: AR_SM_CONST.RECENTER_READY,
          cond: () => {
            return actions.ARPlatform.getPlatform() === AR_PLATFORM.EIGHTHWALL;
          },
          actions: [
            // () =>
            //   actions.Tracker.sendToGa(
            //     "LoadAR",
            //     "",
            //     Math.floor(actions.AssetLoader.getModelSize()).toString(),
            //     `Model added to scene in AR - ${actions.AssetLoader.getModelSize()} MB`,
            //     true
            //   ),
          ],
        },
        {
          target: AR_SM_CONST.POSITION_SEEKING,
          cond: () => {
            return actions.ARPlatform.getPlatform() === AR_PLATFORM.WEBXR;
          },
        },
      ], // do only for eighthwall
    },
    [AR_SM_CONST.POSITION_SEEKING]: {
      entry: [
        () =>
          setTimeout(
            () => actions.ARPageState().setShowCalibration(false),
            500
          ),
        () => actions.ARControllerUIState().setPositionSelection(true),
        () => console.info("POSITION_SEEKING"),
      ],
      on: {
        [AR_POSITION_SEEK.SET_START]: {
          target: AR_SM_CONST.POSITION_SET_DONE,
          actions: [
            (context) => setPositionOnSku(actions, skuId, context.modelId),
            () => actions.InfoPromptState().setShowInfo(true),
          ],
        },
      },
    },
    [AR_SM_CONST.RECENTER_READY]: {
      entry: [
        () => console.info("RECENTER_READY"),
        () =>
          setTimeout(() => actions.ARPageState().setShowRecenter(true), 500),
        (context) => removeSku(actions, skuId, context.modelId),
        () => actions.ARPlatform.startHitTest(hitTestCallback(actions)),
        // () =>
        //   actions.Tracker.sendToGa(
        //     "",
        //     "",
        //     "Initial Interaction Event - AR",
        //     "",
        //     false
        //   ),
        // () => actions.Tracker.registerTimeout(),
      ],
      on: {
        [AR_POSITION_SEEK.RECENTER]: {
          target: AR_SM_CONST.POSITION_SEEKING,
          actions: [
            () => actions.ARPageState().setShowRecenter(false),
            () => recenterScene(actions.ARPlatform.getSceneDetails().camera),
            () => addPositionReticleToScene(actions),
          ],
        },
      },
    },
    [AR_SM_CONST.POSITION_SET_DONE]: {
      entry: [
        () => actions.ARPlatform.stopHitTest(),
        () => actions.ARControllerUIState().setPositionSelection(false),
        (context) => {
          const platform = actions.ARPlatform.getPlatform();
          const skuConfig = actions.ConfigLoader.getSKUConfig(skuId);
          actions.ARControllerUIState().setPositionSelection(false);
          actions.useListnerStore
            .getState()
            .setGestureListner(
              dragFunctionality(
                context.modelId,
                actions,
                actions.ARPlatform.getSceneDetails
              )
            );
          const uiConfig = generateUIConfig(
            context.modelId,
            actions,
            skuConfig,
            XR_MODES.AR,
            platform,
            "RENDERER"
          );
          if (actions.MainStateStore.getGlobalOnLoad() === 0) {
            uiConfig.onLoad();
            actions.MainStateStore.setGlobalOnLoad(1);
          }
          sendAutoEvent(
            actions,
            getXrModefromURL(actions.window),
            "Button Added",
            true
          );
          // actions.Tracker.sendToGa(
          //   "",
          //   "",
          //   "Button Added",
          //   "Button Added To Experience In Ar",
          //   true
          // );
          actions.useSubmenuStore
            .getState()
            .setCorrelationFlag(skuConfig?.iconCorrelationV2 ?? false);
          actions.useSubmenuStore
            .getState()
            .setCorrelationConfig(getCorrelationData(uiConfig));
          actions.useSubmenuStore.getState().setButtonsConfig(uiConfig);
          actions.useButtonConfigStore
            .getState()
            .setUIConfig({ submenu: true });
          console.log("uiconfigAR", uiConfig.onLoad);
        },
      ],
      on: {
        [AR_POSITION_SEEK.POSITION_RESET]: {
          target: AR_SM_CONST.POSITION_SEEK_START,
          actions: [(context) => removeSku(actions, skuId, context.modelId)],
        },
        [AR_SM_CONST.RECENTER_READY]: {
          target: AR_SM_CONST.RECENTER_READY,
        },
      },
    },
  },
});
